// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckSquare,
  Circle,
  Check,
  AlertTriangle,
  Scale,
  Clock,
  Calendar as CalendarIcon,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MobilePageContainer } from '../ui/MobilePageContainer';
import { MobileSearchBar } from '../ui/MobileSearchBar';
import { MobileEmptyState } from '../ui/MobileEmptyState';
import { MobileFab } from '../ui/MobileFab';
import { CreateTaskModal } from '@/components/tasks/CreateTaskModal';

type Task = {
  id: string;
  title: string;
  description?: string;
  due_date?: string;
  priority?: string;
  status?: string;
  case_id?: string | null;
  cases?: { title?: string } | null;
};

type Tab = 'pending' | 'done' | 'overdue' | 'all';

const fmtDate = (s?: string) => {
  if (!s) return '';
  return new Date(s).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PRIORITY_TONE: Record<string, string> = {
  'Baixa': 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  'Média': 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  'Alta': 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  'Urgente': 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  'Crítica': 'bg-red-600/15 text-red-700 dark:text-red-300',
};

const MobileTasks = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pending');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [savingDetail, setSavingDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(
          'id, title, description, due_date, priority, status, case_id, cases(title)',
        )
        .eq('user_id', user.id)
        .order('due_date', { ascending: true, nullsFirst: false })
        .limit(300);
      if (error) throw error;
      setTasks(data || []);
    } catch (err: any) {
      toast({
        title: 'Erro ao carregar tarefas',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleDone = async (t: Task) => {
    const newStatus = t.status === 'Concluída' ? 'Pendente' : 'Concluída';
    const prev = tasks;
    setTasks((cur) =>
      cur.map((x) => (x.id === t.id ? { ...x, status: newStatus } : x)),
    );
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', t.id);
      if (error) throw error;
    } catch (err: any) {
      setTasks(prev);
      toast({
        title: 'Falha ao atualizar',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const openTaskDetail = (t: Task) => {
    setSelectedTask({ ...t });
    setDetailOpen(true);
  };

  const persistTask = async (task: Task) => {
    setSavingDetail(true);
    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          title: task.title,
          description: task.description || null,
          due_date: task.due_date || null,
          priority: task.priority || 'Média',
          status: task.status || 'Pendente',
        })
        .eq('id', task.id);
      if (error) throw error;
      setTasks((cur) => cur.map((x) => (x.id === task.id ? { ...x, ...task } : x)));
      setSelectedTask(task);
      return true;
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar tarefa',
        description: err.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setSavingDetail(false);
    }
  };

  const postponeTaskByHours = async (hours: number) => {
    if (!selectedTask) return;
    const base = selectedTask.due_date ? new Date(selectedTask.due_date) : new Date();
    const next = new Date(base.getTime() + hours * 60 * 60 * 1000);
    const updated = { ...selectedTask, due_date: next.toISOString() };
    const ok = await persistTask(updated);
    if (ok) {
      toast({
        title: 'Tarefa reagendada',
        description: `Prazo ajustado em +${hours}h.`,
      });
    }
  };

  const confirmTaskDone = async () => {
    if (!selectedTask) return;
    const ok = await persistTask({ ...selectedTask, status: 'Concluída' });
    if (ok) {
      toast({ title: 'Tarefa confirmada como concluída' });
      setDetailOpen(false);
    }
  };

  const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    return tasks.filter((t) => {
      const done = t.status === 'Concluída';
      const overdue =
        !done && t.due_date && new Date(t.due_date).getTime() < now;

      if (tab === 'pending' && done) return false;
      if (tab === 'done' && !done) return false;
      if (tab === 'overdue' && !overdue) return false;

      if (!q) return true;
      return [t.title, t.description, t.cases?.title]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [tasks, tab, search]);

  const counts = useMemo(() => {
    const now = Date.now();
    let pending = 0;
    let done = 0;
    let overdue = 0;
    tasks.forEach((t) => {
      const isDone = t.status === 'Concluída';
      if (isDone) done++;
      else pending++;
      if (
        !isDone &&
        t.due_date &&
        new Date(t.due_date).getTime() < now
      ) {
        overdue++;
      }
    });
    return { pending, done, overdue, all: tasks.length };
  }, [tasks]);

  return (
    <MobilePageContainer>
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="px-3 pt-3 pb-2">
          <MobileSearchBar
            value={search}
            onChange={setSearch}
            placeholder="Buscar tarefa..."
          />
        </div>
        <div className="px-3 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: 'pending' as Tab, label: 'Pendentes', count: counts.pending },
            { id: 'overdue' as Tab, label: 'Atrasadas', count: counts.overdue },
            { id: 'done' as Tab, label: 'Concluídas', count: counts.done },
            { id: 'all' as Tab, label: 'Todas', count: counts.all },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border shrink-0 inline-flex items-center gap-1.5 transition-colors',
                tab === t.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border',
              )}
            >
              {t.label}
              <span
                className={cn(
                  'inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-bold',
                  tab === t.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground text-center">
          Carregando...
        </div>
      ) : filtered.length === 0 ? (
        <MobileEmptyState
          className="mt-8"
          icon={<CheckSquare className="h-6 w-6" />}
          title="Nenhuma tarefa por aqui"
          description={
            tab === 'pending'
              ? 'Tudo em dia! Parabéns.'
              : 'Ajuste os filtros ou crie uma nova.'
          }
        />
      ) : (
        <div className="bg-card">
          {filtered.some((t) => t.status !== 'Concluída' && t.due_date && new Date(t.due_date).getTime() < Date.now()) && (
            <div className="mx-3 mt-3 mb-2 rounded-xl border border-rose-300/40 bg-gradient-to-r from-rose-500/10 via-amber-400/10 to-transparent px-3 py-2 shadow-[0_10px_30px_-22px_rgba(244,63,94,0.65)]">
              <p className="text-[11px] font-semibold tracking-wide text-rose-700 dark:text-rose-300 inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                Existem tarefas atrasadas com prioridade de ação
              </p>
            </div>
          )}

          <div className="divide-y divide-border">
          {filtered.map((t) => {
            const done = t.status === 'Concluída';
            const overdue =
              !done && t.due_date && new Date(t.due_date).getTime() < Date.now();
            const highPriorityOverdue =
              overdue &&
              (String(t.priority || '').toLowerCase().includes('alta') ||
                String(t.priority || '').toLowerCase().includes('urg'));
            const pTone = t.priority ? PRIORITY_TONE[t.priority] : null;

            return (
              <div
                key={t.id}
                className={cn(
                  'flex items-start gap-3 px-4 py-3 bg-card active:bg-muted/70',
                  overdue && 'border-l-4 border-rose-400/80 bg-gradient-to-r from-rose-500/[0.05] to-transparent',
                  highPriorityOverdue && 'border-rose-500 shadow-[0_12px_28px_-20px_rgba(244,63,94,0.7)]',
                )}
              >
                <button
                  onClick={() => toggleDone(t)}
                  className={cn(
                    'mt-0.5 h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                    done
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'border-muted-foreground/40',
                  )}
                  aria-label={done ? 'Marcar pendente' : 'Concluir'}
                >
                  {done && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </button>

                <div
                  className="flex-1 min-w-0"
                onClick={() => openTaskDetail(t)}
                >
                  <p
                    className={cn(
                      'font-semibold text-[15px] leading-tight',
                      done && 'line-through text-muted-foreground',
                    )}
                  >
                    {t.title}
                  </p>
                  {t.cases?.title && (
                    <p className="text-[12px] text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                      <Scale className="h-3 w-3" />
                      {t.cases.title}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                    {t.due_date && (
                      <Badge
                        className={cn(
                          'border-0 text-[10px] gap-1',
                          overdue
                            ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {overdue ? (
                          <AlertTriangle className="h-3 w-3" />
                        ) : (
                          <Clock className="h-3 w-3" />
                        )}
                        {fmtDate(t.due_date)}
                      </Badge>
                    )}
                    {pTone && t.priority && (
                      <Badge className={`${pTone} border-0 text-[10px]`}>
                        {t.priority}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      <MobileFab onClick={() => setCreateOpen(true)} />

      <CreateTaskModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        selectedDate={new Date()}
        onTaskCreated={() => {
          setCreateOpen(false);
          load();
        }}
      />

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Detalhes da tarefa</SheetTitle>
          </SheetHeader>
          {selectedTask && (
            <div className="mt-4 space-y-4 pb-6">
              <div className="space-y-1.5">
                <Label>Título</Label>
                <Input
                  value={selectedTask.title || ''}
                  onChange={(e) => setSelectedTask((cur) => cur ? { ...cur, title: e.target.value } : cur)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Descrição</Label>
                <Textarea
                  rows={3}
                  value={selectedTask.description || ''}
                  onChange={(e) => setSelectedTask((cur) => cur ? { ...cur, description: e.target.value } : cur)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Prioridade</Label>
                  <Select
                    value={selectedTask.priority || 'Média'}
                    onValueChange={(value) => setSelectedTask((cur) => cur ? { ...cur, priority: value } : cur)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Baixa">Baixa</SelectItem>
                      <SelectItem value="Média">Média</SelectItem>
                      <SelectItem value="Alta">Alta</SelectItem>
                      <SelectItem value="Urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select
                    value={selectedTask.status || 'Pendente'}
                    onValueChange={(value) => setSelectedTask((cur) => cur ? { ...cur, status: value } : cur)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pendente">Pendente</SelectItem>
                      <SelectItem value="Em andamento">Em andamento</SelectItem>
                      <SelectItem value="Concluída">Concluída</SelectItem>
                      <SelectItem value="Cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Prazo</Label>
                <Input
                  type="datetime-local"
                  value={toLocalInput(selectedTask.due_date)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTask((cur) => cur ? { ...cur, due_date: v ? new Date(v).toISOString() : undefined } : cur);
                  }}
                />
              </div>

              {selectedTask.case_id && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/pwa/processo/${selectedTask.case_id}`)}
                >
                  Ver processo vinculado
                </Button>
              )}

              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={() => postponeTaskByHours(24)} disabled={savingDetail}>
                  Adiar 1d
                </Button>
                <Button variant="outline" onClick={() => postponeTaskByHours(48)} disabled={savingDetail}>
                  Prorrogar 2d
                </Button>
                <Button variant="outline" onClick={() => postponeTaskByHours(168)} disabled={savingDetail}>
                  +7 dias
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => persistTask(selectedTask)} disabled={savingDetail}>
                  {savingDetail ? 'Salvando...' : 'Salvar alterações'}
                </Button>
                <Button
                  variant="secondary"
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={confirmTaskDone}
                  disabled={savingDetail}
                >
                  Confirmar tarefa
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </MobilePageContainer>
  );
};

export default MobileTasks;
